declare global {
    /** HubSpot tracking queue; consumed by js(-eu1).hs-scripts.com once loaded. */
    var _hsq: unknown[] | undefined;
}

const HUBSPOT_SCRIPT_ELEMENT_ID = "hs-script-loader";
const HUBSPOT_ANALYTICS_SCRIPT_ID = "hs-analytics";
const ANALYTICS_WAIT_MS = 8000;

/**
 * HubSpot portal id for local dev when GTM is off. Production loads HubSpot via GTM — leave unset in CI/prod.
 */
export const getHubspotPortalId = (): string | undefined => {
    const id = __VITE_HUBSPOT_PORTAL_ID__.trim();
    return id.length > 0 ? id : undefined;
};

/** Script host, e.g. `js-eu1.hs-scripts.com` for EU portals. */
const hubspotScriptHost = (): string => {
    const host = __VITE_HUBSPOT_SCRIPT_HOST__.trim();
    return host.length > 0 ? host : "js-eu1.hs-scripts.com";
};

export const isHubSpotTrackingScriptPresent = (): boolean =>
    Boolean(document.getElementById(HUBSPOT_SCRIPT_ELEMENT_ID));

/** True once HubSpot has set the visitor cookie (banner accepted + analytics running). */
export const hasHubSpotVisitorCookie = (): boolean =>
    typeof document !== "undefined" && /(?:^|;\s*)hubspotutk=/.test(document.cookie);

const waitForScriptElement = (id: string, timeoutMs: number): Promise<void> =>
    new Promise(resolve => {
        const finish = () => resolve();
        const attach = (script: HTMLScriptElement) => {
            if (script.dataset.hubspotLoaded === "true") {
                finish();
                return;
            }
            script.addEventListener(
                "load",
                () => {
                    script.dataset.hubspotLoaded = "true";
                    finish();
                },
                { once: true }
            );
            script.addEventListener("error", finish, { once: true });
        };

        const existing = document.getElementById(id) as HTMLScriptElement | null;
        if (existing) {
            attach(existing);
            window.setTimeout(finish, timeoutMs);
            return;
        }

        const observer = new MutationObserver(() => {
            const script = document.getElementById(id) as HTMLScriptElement | null;
            if (!script) {
                return;
            }
            observer.disconnect();
            attach(script);
        });
        observer.observe(document.documentElement, { childList: true, subtree: true });
        window.setTimeout(() => {
            observer.disconnect();
            finish();
        }, timeoutMs);
    });

/**
 * Inject HubSpot's tracking script when `VITE_HUBSPOT_PORTAL_ID` is set (local dev without GTM),
 * then wait until `hs-analytics` (not just the loader stub) is ready.
 */
export const ensureHubSpotTrackingLoaded = async (): Promise<void> => {
    const portalId = getHubspotPortalId();
    if (!portalId) {
        return;
    }

    window._hsq = window._hsq || [];

    if (!isHubSpotTrackingScriptPresent()) {
        await new Promise<void>(resolve => {
            const script = document.createElement("script");
            script.id = HUBSPOT_SCRIPT_ELEMENT_ID;
            script.async = true;
            script.defer = true;
            script.src = `https://${hubspotScriptHost()}/${portalId}.js`;
            script.onload = () => resolve();
            script.onerror = () => resolve();
            document.head.appendChild(script);
            window.setTimeout(resolve, ANALYTICS_WAIT_MS);
        });
    }

    await waitForScriptElement(HUBSPOT_ANALYTICS_SCRIPT_ID, ANALYTICS_WAIT_MS);
};

/**
 * Queue identify + page view on the current URL. `setPath` helps SPAs and strips nothing —
 * HubSpot merges query params per portal settings.
 */
export const queueHubSpotIdentifyAndPageView = (email: string): void => {
    window._hsq = window._hsq || [];
    const path = `${window.location.pathname}${window.location.search}`;
    window._hsq.push(["identify", { email }]);
    window._hsq.push(["setPath", path]);
    window._hsq.push(["trackPageView"]);
};
